import { createSlice } from "@reduxjs/toolkit";

import { navigateToBooking } from "./initialDateSlice";

// The party and length picked in the "Add booking" dialog, carried to the grid where the admin
// then clicks a time and table. Deliberately has no date: the booking lands on whichever day the
// grid is showing when the admin continues, so day navigation stays available mid-flow.
export interface ManualBookingRequest {
    adults: number;
    kids: number;
    highchairs: number;
    durationMinutes: number;
}

interface DashboardEventsState {
    isWalkinRequested: boolean;
    walkinGuestCount: number;
    walkinDurationMinutes: number;
    isAssigningTable: boolean;
    manualBookingRequest: ManualBookingRequest | null;
    // Incremented every time a "save or cancel first" warning should be shown.
    // A single snackbar subscribes to this so any guarded transition — from any
    // subtree — can raise the warning without local snackbar state.
    guardWarningNonce: number;
}

const initialState: DashboardEventsState = {
    isWalkinRequested: false,
    walkinGuestCount: 0,
    walkinDurationMinutes: 0,
    isAssigningTable: false,
    manualBookingRequest: null,
    guardWarningNonce: 0,
};

const clearPendingTableFlows = (state: DashboardEventsState) => {
    state.isWalkinRequested = false;
    state.walkinGuestCount = 0;
    state.walkinDurationMinutes = 0;
    state.manualBookingRequest = null;
};

export const dashboardEventsSlice = createSlice({
    name: "dashboardEvents",
    initialState,
    reducers: {
        requestWalkin: (state, action: { payload: { count: number; durationMinutes: number } }) => {
            state.isWalkinRequested = true;
            state.walkinGuestCount = action.payload.count;
            state.walkinDurationMinutes = action.payload.durationMinutes;
        },
        resetWalkinRequest: (state) => {
            state.isWalkinRequested = false;
            state.walkinGuestCount = 0;
            state.walkinDurationMinutes = 0;
        },
        // Lets the admin drag-resize the not-yet-saved walk-in chip's duration in the grid,
        // before ever clicking Save Assignment — updates the same field the guest-count dialog
        // seeded, so every dropped chip (and the final assignWalkInTable call) picks it up.
        setWalkinDurationMinutes: (state, action: { payload: number }) => {
            state.walkinDurationMinutes = action.payload;
        },
        requestManualBooking: (state, action: { payload: ManualBookingRequest }) => {
            state.manualBookingRequest = action.payload;
        },
        resetManualBookingRequest: (state) => {
            state.manualBookingRequest = null;
        },
        // The admin has left the dashboard. These requests live in Redux, which outlives the page,
        // so without this they come back with the admin: a stale walk-in made "Add walk-in" warn
        // "save or cancel first" on return, and a half-finished "Add booking" was still in progress.
        resetPendingTableFlows: clearPendingTableFlows,
        // Same role as setWalkinDurationMinutes: the admin drag-resizes a not-yet-saved chip.
        setManualBookingDurationMinutes: (state, action: { payload: number }) => {
            if (state.manualBookingRequest) {
                state.manualBookingRequest.durationMinutes = action.payload;
            }
        },
        setTableAssignmentActive: (state, action: { payload: boolean }) => {
            state.isAssigningTable = action.payload;
        },
        raiseSaveOrCancelWarning: (state) => {
            state.guardWarningNonce += 1;
        },
    },
    extraReducers: (builder) => {
        // Opening a booking from a notification or the search remounts the dashboard on that
        // booking's day; a walk-in or "Add booking" in progress can't carry on there.
        builder.addCase(navigateToBooking, clearPendingTableFlows);
    },
});

export const {
    requestWalkin,
    resetWalkinRequest,
    setWalkinDurationMinutes,
    requestManualBooking,
    resetManualBookingRequest,
    resetPendingTableFlows,
    setManualBookingDurationMinutes,
    setTableAssignmentActive,
    raiseSaveOrCancelWarning,
} = dashboardEventsSlice.actions;

export default dashboardEventsSlice.reducer;
